import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AccessibilityToolbar.css';

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  invertColors: boolean;
  grayscale: boolean;
  readableFont: boolean;
  highlightLinks: boolean;
  textAlign: boolean;
  hideImages: boolean;
  pauseAnimations: boolean;
  bigCursor: boolean;
  colorBlindness: string;
}

const AccessibilityToolbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>({
    fontSize: 100,
    highContrast: false,
    invertColors: false,
    grayscale: false,
    readableFont: false,
    highlightLinks: false,
    textAlign: false,
    hideImages: false,
    pauseAnimations: false,
    bigCursor: false,
    colorBlindness: 'none',
  });

  // Load settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('a11y-settings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  }, []);

  // Save settings to localStorage and apply them
  useEffect(() => {
    localStorage.setItem('a11y-settings', JSON.stringify(settings));
    applySettings();
  }, [settings]);

  const applySettings = () => {
    const root = document.documentElement;

    // Font size
    root.style.setProperty('--a11y-font-size', `${settings.fontSize}`);
    document.body.classList.toggle('a11y-font-adjusted', settings.fontSize !== 100);

    // Toggle classes
    document.body.classList.toggle('a11y-high-contrast', settings.highContrast);
    document.body.classList.toggle('a11y-invert-colors', settings.invertColors);
    document.body.classList.toggle('a11y-grayscale', settings.grayscale);
    document.body.classList.toggle('a11y-readable-font', settings.readableFont);
    document.body.classList.toggle('a11y-highlight-links', settings.highlightLinks);
    document.body.classList.toggle('a11y-text-align-left', settings.textAlign);
    document.body.classList.toggle('a11y-hide-images', settings.hideImages);
    document.body.classList.toggle('a11y-pause-animations', settings.pauseAnimations);
    document.body.classList.toggle('a11y-big-cursor', settings.bigCursor);

    // Color blindness filters
    document.body.classList.remove('a11y-protanopia', 'a11y-deuteranopia', 'a11y-tritanopia', 'a11y-achromatopsia');
    if (settings.colorBlindness !== 'none') {
      document.body.classList.add(`a11y-${settings.colorBlindness}`);
    }
  };

  const resetSettings = () => {
    setSettings({
      fontSize: 100,
      highContrast: false,
      invertColors: false,
      grayscale: false,
      readableFont: false,
      highlightLinks: false,
      textAlign: false,
      hideImages: false,
      pauseAnimations: false,
      bigCursor: false,
      colorBlindness: 'none',
    });
  };

  const toggleSetting = (key: keyof AccessibilitySettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        className="a11y-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle Accessibility Toolbar"
        title="Accessibility Options"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
        </svg>
      </motion.button>

      {/* Toolbar Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="a11y-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Panel */}
            <motion.div
              className="a11y-panel"
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
            >
              <div className="a11y-header">
                <h2>Accessibility Options</h2>
                <button
                  className="a11y-close-btn"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="a11y-content">
                {/* Text Adjustments */}
                <div className="a11y-section">
                  <h3>Text Adjustments</h3>

                  <div className="a11y-control">
                    <label>Text Size</label>
                    <div className="a11y-btn-group a11y-size-group">
                      <button
                        className={settings.fontSize === 90 ? 'active' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, fontSize: 90 }))}
                        style={{ fontSize: '12px' }}
                        aria-label="Smaller text"
                      >
                        aA
                      </button>
                      <button
                        className={`${settings.fontSize === 100 ? 'active' : ''} ${settings.fontSize === 100 ? 'default-option' : ''}`}
                        onClick={() => setSettings(prev => ({ ...prev, fontSize: 100 }))}
                        style={{ fontSize: '16px' }}
                        aria-label="Normal text (default)"
                      >
                        aA
                      </button>
                      <button
                        className={settings.fontSize === 110 ? 'active' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, fontSize: 110 }))}
                        style={{ fontSize: '20px' }}
                        aria-label="Bigger text"
                      >
                        aA
                      </button>
                    </div>
                  </div>
                </div>

                {/* Color & Contrast */}
                <div className="a11y-section">
                  <h3>Color & Contrast</h3>

                  <button
                    className={`a11y-toggle ${settings.highContrast ? 'active' : ''}`}
                    onClick={() => toggleSetting('highContrast')}
                  >
                    <span>High Contrast</span>
                    <span className="a11y-toggle-indicator">{settings.highContrast ? '✓' : ''}</span>
                  </button>

                  <button
                    className={`a11y-toggle ${settings.invertColors ? 'active' : ''}`}
                    onClick={() => toggleSetting('invertColors')}
                  >
                    <span>Invert Colors</span>
                    <span className="a11y-toggle-indicator">{settings.invertColors ? '✓' : ''}</span>
                  </button>

                  <button
                    className={`a11y-toggle ${settings.grayscale ? 'active' : ''}`}
                    onClick={() => toggleSetting('grayscale')}
                  >
                    <span>Grayscale</span>
                    <span className="a11y-toggle-indicator">{settings.grayscale ? '✓' : ''}</span>
                  </button>
                </div>

                {/* Content */}
                <div className="a11y-section">
                  <h3>Content</h3>

                  <button
                    className={`a11y-toggle ${settings.readableFont ? 'active' : ''}`}
                    onClick={() => toggleSetting('readableFont')}
                  >
                    <span>Readable Font</span>
                    <span className="a11y-toggle-indicator">{settings.readableFont ? '✓' : ''}</span>
                  </button>

                  <button
                    className={`a11y-toggle ${settings.highlightLinks ? 'active' : ''}`}
                    onClick={() => toggleSetting('highlightLinks')}
                  >
                    <span>Highlight Links</span>
                    <span className="a11y-toggle-indicator">{settings.highlightLinks ? '✓' : ''}</span>
                  </button>

                  <button
                    className={`a11y-toggle ${settings.textAlign ? 'active' : ''}`}
                    onClick={() => toggleSetting('textAlign')}
                  >
                    <span>Align Text Left</span>
                    <span className="a11y-toggle-indicator">{settings.textAlign ? '✓' : ''}</span>
                  </button>

                  <button
                    className={`a11y-toggle ${settings.hideImages ? 'active' : ''}`}
                    onClick={() => toggleSetting('hideImages')}
                  >
                    <span>Hide Images</span>
                    <span className="a11y-toggle-indicator">{settings.hideImages ? '✓' : ''}</span>
                  </button>

                  <button
                    className={`a11y-toggle ${settings.pauseAnimations ? 'active' : ''}`}
                    onClick={() => toggleSetting('pauseAnimations')}
                  >
                    <span>Pause Animations</span>
                    <span className="a11y-toggle-indicator">{settings.pauseAnimations ? '✓' : ''}</span>
                  </button>
                </div>

                {/* Navigation */}
                <div className="a11y-section">
                  <h3>Navigation</h3>

                  <button
                    className={`a11y-toggle ${settings.bigCursor ? 'active' : ''}`}
                    onClick={() => toggleSetting('bigCursor')}
                  >
                    <span>Big Cursor</span>
                    <span className="a11y-toggle-indicator">{settings.bigCursor ? '✓' : ''}</span>
                  </button>
                </div>

                {/* Color Blindness */}
                <div className="a11y-section">
                  <h3>Color Blindness</h3>

                  <div className="a11y-control">
                    <label>Filter Type</label>
                    <div className="a11y-btn-group">
                      <button
                        className={settings.colorBlindness === 'none' ? 'active default-option' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, colorBlindness: 'none' }))}
                        aria-label="No filter (default)"
                      >
                        None
                      </button>
                      <button
                        className={settings.colorBlindness === 'protanopia' ? 'active' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, colorBlindness: 'protanopia' }))}
                        aria-label="Protanopia (Red-blind)"
                        title="Red-blind"
                      >
                        Protanopia
                      </button>
                      <button
                        className={settings.colorBlindness === 'deuteranopia' ? 'active' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, colorBlindness: 'deuteranopia' }))}
                        aria-label="Deuteranopia (Green-blind)"
                        title="Green-blind"
                      >
                        Deuteranopia
                      </button>
                    </div>
                    <div className="a11y-btn-group">
                      <button
                        className={settings.colorBlindness === 'tritanopia' ? 'active' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, colorBlindness: 'tritanopia' }))}
                        aria-label="Tritanopia (Blue-blind)"
                        title="Blue-blind"
                      >
                        Tritanopia
                      </button>
                      <button
                        className={settings.colorBlindness === 'achromatopsia' ? 'active' : ''}
                        onClick={() => setSettings(prev => ({ ...prev, colorBlindness: 'achromatopsia' }))}
                        aria-label="Achromatopsia (Complete color blindness)"
                        title="Complete color blindness"
                      >
                        Achromatopsia
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reset */}
                <div className="a11y-section">
                  <button className="a11y-reset-btn" onClick={resetSettings}>
                    Reset All Settings
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AccessibilityToolbar;
